import { Warning } from '@mui/icons-material';
import { find, isNull } from 'lodash';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { shopsApi } from 'api';
import { Math } from 'classes';
import {
  CommissionInfo,
  DataGrid,
  DataGridColumnDefinition,
  DataWrapper,
  IntervalCommissionsInfoDialog,
  PaginationWrapper,
  QueryFilters,
  StylizedNumber,
} from 'components';
import { FilterDefinitionType, OrderType, QueryKey } from 'enums';
import { usePartialQuery, useUserContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  Shop,
  P2PProvider,
  CascadesCashbackData,
  IntervalCommission,
  User,
} from 'types';
import { formUtils, userUtils } from 'utils';

import { AgentsInfo } from '../AgentsInfo';

type Props = {
  orderType: OrderType;
  shops?: Shop[];
  users?: User[];
};

type CascadesCashbackFilters = {
  cascadeId: string;
  shopId: string;
  p2pProviderId: string;
};

export const CascadesCashback: React.FC<Props> = ({
  shops,
  users,
  orderType,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.cashback.cascades',
  });

  const { p2pProviders, cascades } = useUserContext();

  const getP2PProviderName = useCallback(
    (id: string) => find(p2pProviders, { id })?.name || id,
    [p2pProviders],
  );

  const getCascadeName = useCallback(
    (id: string) => find(cascades, { id })?.name || id,
    [cascades],
  );

  const [
    intervalCommissionsInfoDialogProps,
    setIntervalCommissionsInfoDialogOpen,
  ] = useState<{
    open: boolean;
    intervalCommissions?: IntervalCommission[];
  }>({ open: false });

  const isPayin = useMemo(() => orderType === OrderType.Payin, [orderType]);

  const filtersDefinitions: FilterDefinition<CascadesCashbackFilters>[] =
    useMemo(
      () => [
        {
          label: t('columns.cascade'),
          name: 'cascadeId',
          type: FilterDefinitionType.Select,
          options: formUtils.getOptions(cascades),
          getDisplayName: getCascadeName,
        },
        {
          label: t('columns.shop'),
          name: 'shopId',
          type: FilterDefinitionType.Shop,
          shops,
        },
        {
          label: t('columns.p2p_provider'),
          name: 'p2pProviderId',
          type: FilterDefinitionType.Select,
          options: formUtils.getOptions(p2pProviders),
          getDisplayName: getP2PProviderName,
        },
      ],
      [shops, t, p2pProviders, getP2PProviderName, cascades, getCascadeName],
    );

  const queryResult = usePartialQuery(
    [QueryKey.CascadesCashback, orderType],
    shopsApi.getCascadesCashbackForOrderType(orderType),
  );

  const getP2PProviderCommission = useCallback(
    (p2pProvider: Partial<P2PProvider>): number =>
      p2pProvider[isPayin ? 'payinConfig' : 'payoutConfig']?.expectedFee || 0,
    [isPayin],
  );

  const getShopCommission = useCallback(
    (item: CascadesCashbackData) =>
      (isPayin ? item.commission : item.payoutCommission) || 0,
    [isPayin],
  );

  const getPlatformCommission = useCallback(
    (item: CascadesCashbackData): number | null => {
      const p2pProvider = item.cascade.p2pProvidersConfigs?.[0].p2pProvider;
      return p2pProvider
        ? new Math(getShopCommission(item)).minus(
            userUtils.getAgentsCommission(item.agents || [], isPayin),
            getP2PProviderCommission(p2pProvider),
          ).value
        : null;
    },
    [getShopCommission, getP2PProviderCommission, isPayin],
  );

  const columns: DataGridColumnDefinition<CascadesCashbackData>[] = useMemo(
    () => [
      {
        header: '',
        valueGetter: (item) => {
          const commission = getPlatformCommission(item);
          if (isNull(commission)) {
            return null;
          } else {
            return commission < 0 ? <Warning color="warning" /> : null;
          }
        },
      },
      {
        header: t('columns.cascade'),
        valueGetter: (item) => <div>{item.cascade.name}</div>,
      },
      {
        header: t('columns.shop'),
        valueGetter: (item) => (
          <div className="tw-flex">
            <div className="tw-mr-2">{item.name}</div>
            <CommissionInfo
              onIntervalCommissionsButtonClick={(e: React.SyntheticEvent) => {
                e.stopPropagation();
                setIntervalCommissionsInfoDialogOpen({
                  open: true,
                  intervalCommissions: item.intervalCommissions,
                });
              }}
              {...(isPayin
                ? {
                    commission: getShopCommission(item),
                    intervalCommissions: item.intervalCommissions,
                  }
                : { payoutCommission: getShopCommission(item) })}
            />
          </div>
        ),
      },
      {
        header: t('columns.shop_agents'),
        valueGetter: (item) => (
          <AgentsInfo agents={item.agents} isPayin={isPayin} users={users} />
        ),
      },
      {
        header: t('columns.p2p_provider'),
        valueGetter: (item) => {
          const p2pProvider = item.cascade.p2pProvidersConfigs?.[0].p2pProvider;
          return p2pProvider ? (
            <div className="tw-flex">
              <div className="tw-pr-2">{p2pProvider.name}</div>
              <StylizedNumber
                value={getP2PProviderCommission(p2pProvider)}
                unit="%"
              />
            </div>
          ) : null;
        },
      },
      {
        header: t('columns.result_commission'),
        valueGetter: (item) => {
          const commission = getPlatformCommission(item);
          return isNull(commission) ? (
            '-'
          ) : (
            <StylizedNumber value={commission} unit="%" />
          );
        },
      },
    ],
    [
      getPlatformCommission,
      getShopCommission,
      getP2PProviderCommission,
      t,
      setIntervalCommissionsInfoDialogOpen,
      isPayin,
      users,
    ],
  );

  return (
    <Fragment>
      <QueryFilters filtersDefinitions={filtersDefinitions} />
      <DataWrapper queryResult={queryResult}>
        <Fragment>
          <PaginationWrapper
            queryResult={queryResult}
            ignoreState={{ refetchLoading: true }}
          >
            <DataGrid columns={columns} data={queryResult.data?.items} />
          </PaginationWrapper>
          <IntervalCommissionsInfoDialog
            {...intervalCommissionsInfoDialogProps}
            onClose={() =>
              setIntervalCommissionsInfoDialogOpen({ open: false })
            }
          />
        </Fragment>
      </DataWrapper>
    </Fragment>
  );
};
